import React, { useMemo, useState } from "react";
import {
    Badge, Box, Button, Checkbox, Collapse, Divider,
    IconButton, List, ListItemButton, ListItemText,
    Menu, MenuItem, Tooltip, Typography,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ClearIcon from "@mui/icons-material/Clear";
import type { SearchData } from "../../models/SearchElements";
import type { ValuationType } from "../../models/SearchElements";
import { ARTWORK_STATUSES } from "../../functions/ArtworkStatus";

// --- Preset definitions -----------------------------------------------------
// These are just curated shortcuts onto fields that already exist on SearchData
// (statusIds, acquiredFrom/To, minValue, valuationType, *ValueStatus) — nothing
// here introduces new server-side filtering, it's all client-side like the rest
// of the gallery filters. `statusIds` is also editable directly via the Status
// field in DrawerSearch — both write to the same SearchData.statusIds.

type StatusPreset = {
    id: string;
    label: string;
    statusIds: number[];
};

const STATUS_PRESETS: StatusPreset[] = [
    { id: "available", label: "Available For Sale", statusIds: [1] },
    { id: "on-loan", label: "On Loan (In or Out)", statusIds: [10, 11] },
    { id: "on-display", label: "On Display / Exhibition", statusIds: [21] },
    { id: "auction", label: "Consigned for Auction", statusIds: [5] },
    { id: "needs-attention", label: "Needs Attention", statusIds: [30, 31, 32, 33, 34] },
];

const LOGISTICS_PRESETS: StatusPreset[] = [
    { id: "in-storage", label: "In Storage", statusIds: [24] },
    { id: "with-restorer", label: "With Restorer / Framer", statusIds: [23] },
    { id: "in-transit", label: "In Transit", statusIds: [22] },
];

type RecencyPreset = {
    id: string;
    label: string;
    days: number;
};

const RECENCY_PRESETS: RecencyPreset[] = [
    { id: "last-30", label: "Last 30 Days", days: 30 },
    { id: "last-90", label: "Last 90 Days", days: 90 },
];

function isoDaysAgo(days: number): string {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
}

function sameStatusSet(a: number[] | null | undefined, b: number[]): boolean {
    if (!a || a.length !== b.length) return false;
    const sa = [...a].sort((x, y) => x - y);
    const sb = [...b].sort((x, y) => x - y);
    return sa.every((v, i) => v === sb[i]);
}

export type QuickFilterMenuProps = {
    filterParams: SearchData;
    onChange: (params: SearchData) => void;
    hasFinanceRole: boolean | null;
    valueViewOption: ValuationType;
    highValueThreshold?: number;
};

const QuickFilterMenu: React.FC<QuickFilterMenuProps> = ({
    filterParams,
    onChange,
    hasFinanceRole,
    valueViewOption,
    highValueThreshold = 50000,
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [statusExpanded, setStatusExpanded] = useState(false);
    const open = Boolean(anchorEl);

    const valuePresenceField: "acquisitionValueStatus" | "insuranceValueStatus" | "fairMarketValueStatus" =
        valueViewOption === "insurance"
            ? "insuranceValueStatus"
            : valueViewOption === "fairMarketValue"
                ? "fairMarketValueStatus"
                : "acquisitionValueStatus";

    const activeCount = useMemo(() => {
        let count = 0;
        if (filterParams.statusIds?.length) count += 1;
        if (filterParams.acquiredFrom) count += 1;
        if (hasFinanceRole && filterParams.minValue) count += 1;
        if (hasFinanceRole && filterParams[valuePresenceField] === "without") count += 1;
        return count;
    }, [filterParams, hasFinanceRole, valuePresenceField]);

    const toggleStatusPreset = (preset: StatusPreset) => {
        const isActive = sameStatusSet(filterParams.statusIds, preset.statusIds);
        onChange({ ...filterParams, statusIds: isActive ? null : preset.statusIds });
    };

    const toggleIndividualStatus = (statusId: number) => {
        const current = filterParams.statusIds ?? [];
        const next = current.includes(statusId)
            ? current.filter((id) => id !== statusId)
            : [...current, statusId];
        onChange({ ...filterParams, statusIds: next.length ? next : null });
    };

    const toggleRecencyPreset = (preset: RecencyPreset) => {
        const isActive = filterParams.acquiredFrom === isoDaysAgo(preset.days) && !filterParams.acquiredTo;
        onChange({
            ...filterParams,
            acquiredFrom: isActive ? null : isoDaysAgo(preset.days),
            acquiredTo: isActive ? filterParams.acquiredTo : null,
        });
    };

    const toggleHighValue = () => {
        const isActive = filterParams.minValue === highValueThreshold;
        onChange({
            ...filterParams,
            minValue: isActive ? null : highValueThreshold,
            valuationType: isActive ? filterParams.valuationType : (filterParams.valuationType ?? valueViewOption),
        });
    };

    const toggleNoValueRecorded = () => {
        const isActive = filterParams[valuePresenceField] === "without";
        onChange({ ...filterParams, [valuePresenceField]: isActive ? "any" : "without" } as SearchData);
    };

    const clearAll = () => {
        onChange({
            ...filterParams,
            statusIds: null,
            acquiredFrom: null,
            acquiredTo: null,
            ...(hasFinanceRole
                ? { minValue: null, acquisitionValueStatus: "any", insuranceValueStatus: "any", fairMarketValueStatus: "any" }
                : {}),
        });
    };

    return (
        <>
            <Tooltip title="Quick filters">
                <Badge color="primary" badgeContent={activeCount} invisible={activeCount === 0}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FilterAltIcon fontSize="small" />}
                        onClick={(e) => setAnchorEl(e.currentTarget)}
                    >
                        Filters
                    </Button>
                </Badge>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                PaperProps={{ sx: { width: 300 } }}
            >
                <Box sx={{ px: 2, pt: 1, pb: 0.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography variant="subtitle2">Quick Filters</Typography>
                    {activeCount > 0 && (
                        <Tooltip title="Clear all quick filters">
                            <IconButton size="small" onClick={clearAll}>
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>

                <Typography variant="caption" sx={{ px: 2, color: "text.secondary" }}>Status</Typography>
                {STATUS_PRESETS.map((preset) => (
                    <MenuItem key={preset.id} onClick={() => toggleStatusPreset(preset)} dense>
                        <Checkbox
                            edge="start"
                            size="small"
                            checked={sameStatusSet(filterParams.statusIds, preset.statusIds)}
                            tabIndex={-1}
                            disableRipple
                        />
                        <ListItemText primary={preset.label} />
                    </MenuItem>
                ))}
                <ListItemButton dense onClick={() => setStatusExpanded((v) => !v)}>
                    <ListItemText primary="More statuses…" />
                    {statusExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </ListItemButton>
                <Collapse in={statusExpanded} timeout="auto" unmountOnExit>
                    <List dense disablePadding sx={{ maxHeight: 240, overflowY: "auto" }}>
                        {ARTWORK_STATUSES.map((status) => (
                            <MenuItem
                                key={status.artworkStatusId}
                                dense
                                onClick={() => toggleIndividualStatus(status.artworkStatusId)}
                            >
                                <Checkbox
                                    edge="start"
                                    size="small"
                                    checked={(filterParams.statusIds ?? []).includes(status.artworkStatusId)}
                                    tabIndex={-1}
                                    disableRipple
                                />
                                <ListItemText primary={status.name} />
                            </MenuItem>
                        ))}
                    </List>
                </Collapse>

                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" sx={{ px: 2, color: "text.secondary" }}>Recently Acquired</Typography>
                {RECENCY_PRESETS.map((preset) => (
                    <MenuItem key={preset.id} dense onClick={() => toggleRecencyPreset(preset)}>
                        <Checkbox
                            edge="start"
                            size="small"
                            checked={filterParams.acquiredFrom === isoDaysAgo(preset.days) && !filterParams.acquiredTo}
                            tabIndex={-1}
                            disableRipple
                        />
                        <ListItemText primary={preset.label} />
                    </MenuItem>
                ))}

                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" sx={{ px: 2, color: "text.secondary" }}>Logistics</Typography>
                {LOGISTICS_PRESETS.map((preset) => (
                    <MenuItem key={preset.id} dense onClick={() => toggleStatusPreset(preset)}>
                        <Checkbox
                            edge="start"
                            size="small"
                            checked={sameStatusSet(filterParams.statusIds, preset.statusIds)}
                            tabIndex={-1}
                            disableRipple
                        />
                        <ListItemText primary={preset.label} />
                    </MenuItem>
                ))}

                {/* Value filters are financial in nature — restricted to finance-capable roles,
                    matching the gating already used for the Portfolio Value card on this page. */}
                {hasFinanceRole && (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" sx={{ px: 2, color: "text.secondary" }}>Value</Typography>
                        <MenuItem dense onClick={toggleHighValue}>
                            <Checkbox
                                edge="start"
                                size="small"
                                checked={filterParams.minValue === highValueThreshold}
                                tabIndex={-1}
                                disableRipple
                            />
                            <ListItemText primary={`High Value (over ${highValueThreshold.toLocaleString()})`} />
                        </MenuItem>
                        <MenuItem dense onClick={toggleNoValueRecorded}>
                            <Checkbox
                                edge="start"
                                size="small"
                                checked={filterParams[valuePresenceField] === "without"}
                                tabIndex={-1}
                                disableRipple
                            />
                            <ListItemText primary="No Value Recorded" />
                        </MenuItem>
                    </>
                )}
            </Menu>
        </>
    );
};

export default QuickFilterMenu;
